import React, {
  Dispatch,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useRef,
  useState
} from "react";
import {
  Autocomplete,
  Button,
  Grid,
  TextField,
  Stack
} from "@mui/material";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import Modal from "../../../Components/Modal";
import { accessProfileToSelectList, users } from "../../../Requests";
import useGlobalContext from "../../../Hooks/useGlobalContext";
import { useSnackbar } from "notistack";
import PasswordField from "./PasswordField";

interface IOptions {
  id: string;
  label: string;
}

interface IPreserveUserProps {
  reloadResourceTable: () => void;
  openModal: boolean;
  accessProfilesOptions?: IOptions[];
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  idUserEdit: number | null;
}

interface Ifield<T> {
  value: T;
  helperText: string;
  error: boolean;
}

const PreserveUser: React.FC<IPreserveUserProps> = ({
  reloadResourceTable,
  openModal,
  accessProfilesOptions: defaultOptions = [],
  setOpenModal,
  idUserEdit
}) => {
  const { setLoading, loading } = useGlobalContext();
  const { enqueueSnackbar } = useSnackbar();

  const inputRef = useRef<HTMLInputElement>(null);

  const [enableButton, setEnableButton] = useState(false);
  const [accessProfilesOptions, setAccessProfilesOptions] = useState<IOptions[]>(defaultOptions);

  const [name, setName] = useState<Ifield<string>>({ value: "", helperText: "", error: false });
  const [email, setEmail] = useState<Ifield<string>>({ value: "", helperText: "", error: false });
  const [accessProfile, setAccessProfile] = useState<Ifield<IOptions>>({
    value: { id: "", label: "Selecione uma opção" },
    helperText: "",
    error: false
  });
  const [password, setPassword] = useState<Ifield<string>>({ value: "", helperText: "", error: false });
  const [passwordConfirm, setPasswordConfirm] = useState<Ifield<string>>({ value: "", helperText: "", error: false });

  const handleChange = {
    name: (e: React.ChangeEvent<HTMLInputElement>) =>
      setName({ value: e.target.value, helperText: "", error: false }),

    email: (e: React.ChangeEvent<HTMLInputElement>) =>
      setEmail({ value: e.target.value, helperText: "", error: false }),

    profile: (_: SyntheticEvent, value: IOptions | null) =>
      value && setAccessProfile({ value, helperText: "", error: false }),

    password: (val: string) => {
      setPassword({ value: val, helperText: "", error: false });
      const mismatch = val !== passwordConfirm.value;
      setPasswordConfirm((prev) => ({
        ...prev,
        error: mismatch,
        helperText: mismatch ? "As senhas digitadas não são iguais" : ""
      }));
    },

    passwordConfirm: (val: string) => {
      const mismatch = password.value !== val;
      setPasswordConfirm({
        value: val,
        error: mismatch,
        helperText: mismatch ? "As senhas digitadas não são iguais" : ""
      });
    }
  };

  const fetchProfilesIfNeeded = async () => {
    if (accessProfilesOptions.length === 0) {
      const result = await accessProfileToSelectList.get();
      const options = (result || []).map((p: any) => ({ id: p.id, label: p.description }));
      setAccessProfilesOptions(options);
    }
  };

  const validateFields = () => {
    return (
      !!name.value &&
      !!email.value &&
      !!accessProfile.value.id &&
      (idUserEdit !== null || !!password.value) &&
      password.value === passwordConfirm.value
    );
  };

  const updateButtonState = () => setEnableButton(validateFields());

  const handleSave = async () => {
    setLoading?.(true);

    const payload: any = {
      name: name.value,
      email: email.value,
      access_profile_id: accessProfile.value.id
    };
    if (password.value) payload.password = password.value;

    const result = idUserEdit === null
      ? await users.post(payload)
      : await users.put(`${idUserEdit}`, payload);

    if (result?.errors) {
      if (result.errors.name) setName({ value: name.value, helperText: result.errors.name, error: true });
      if (result.errors.email) setEmail({ value: email.value, helperText: result.errors.email, error: true });
      if (result.errors.password) {
        setPassword({ value: "", helperText: result.errors.password, error: true });
      } else {
        setPassword({ value: "", helperText: "", error: false });
      }
      setPasswordConfirm({ value: "", helperText: "", error: false });
      setEnableButton(false);
    }

    if (result?.identifier || result?.description) {
      enqueueSnackbar("Usuário salvo com sucesso!", { variant: "success" });
      handleCloseModal();
      reloadResourceTable();
    } else if (result?.message) {
      enqueueSnackbar(result.message, { variant: "error" });
    }

    setLoading?.(false);
  };

  const handleCloseModal = () => setOpenModal(false);

  const reloadForm = async () => {
    setName({ value: "", helperText: "", error: false });
    setEmail({ value: "", helperText: "", error: false });
    setAccessProfile({ value: { id: "", label: "Selecione uma opção" }, helperText: "", error: false });
    setPassword({ value: "", helperText: "", error: false });
    setPasswordConfirm({ value: "", helperText: "", error: false });

    if (idUserEdit) {
      const { data } = await users.get(`${idUserEdit}`);
      const option = accessProfilesOptions.find((n) => n.id === data.access_profile_id);
      setName({ value: data.name, helperText: "", error: false });
      setEmail({ value: data.email, helperText: "", error: false });
      setAccessProfile({ value: option as IOptions, helperText: "", error: false });
    }

    setTimeout(() => inputRef?.current?.focus(), 100);
    setLoading?.(false);
  };

  useEffect(() => {
    if (openModal) reloadForm();
  }, [openModal]);

  useEffect(() => {
    fetchProfilesIfNeeded();
  }, []);

  useEffect(() => {
    updateButtonState();
  }, [name, email, accessProfile, password, passwordConfirm]);

  return (
    <Modal
      title={idUserEdit ? "Editar dados do usuário" : "Adicionar novo usuário"}
      open={openModal && !loading}
      closeModal={handleCloseModal}
      maxWidth="sm"
    >
      <Grid container spacing={0.5}>
        <Grid item xs={12}>
          <TextField
            inputRef={inputRef}
            id="name"
            label="Nome"
            variant="filled"
            fullWidth
            value={name.value}
            onChange={handleChange.name}
            error={name.error}
            helperText={name.helperText}
            autoComplete="off"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            id="email"
            label="E-mail"
            variant="filled"
            fullWidth
            value={email.value}
            onChange={handleChange.email}
            error={email.error}
            helperText={email.helperText}
            autoComplete="off"
          />
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            id="accessProfile"
            options={accessProfilesOptions}
            getOptionLabel={(option: IOptions) => option?.label || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable
            noOptionsText="Sem opções disponíveis"
            value={accessProfile.value}
            onChange={handleChange.profile}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Perfil de Acesso"
                variant="filled"
                fullWidth
                error={accessProfile.error}
                helperText={accessProfile.helperText}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <PasswordField
            id="password"
            label="Senha"
            onChange={handleChange.password}
            error={password.error}
            helperText={password.helperText}
          />
        </Grid>

        <Grid item xs={12}>
          <PasswordField
            id="passwordConfirm"
            label="Confirmação da Senha"
            onChange={handleChange.passwordConfirm}
            error={passwordConfirm.error}
            helperText={passwordConfirm.helperText}
          />
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              disabled={!enableButton}
              onClick={handleSave}
              startIcon={<SaveAsIcon />}
              aria-label="Salvar usuário"
            >
              Salvar
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Modal>
  );
};

export type { IOptions };
export default PreserveUser;
